import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAzureStorageBlobs } from '../hooks';
import { addNotification, blobFolderUriBuilder, blobNameToTimeDistribution, deleteNotification, getNotificationSocketTopicRecipients, initializeDOMPurify, logger, notificationToTimeDistribution, } from '../utils';
import { NotificationTimeDistributionTypes } from '../types';
import { ActiveNotificationList } from './ActiveNotificationList';
import { SubscribeSocketTopic } from '@topconagriculture/react-socket-notification';
import { PLATFORM_NOTIFICATIONS_DELETE_SOCKET_TOPIC, PLATFORM_NOTIFICATIONS_SOCKET_TOPIC, STORAGE_FOLDER_VIRTUAL_DELIMITER, } from '../config';
export const PlatformNotifications = ({ storageContainer, storageUrl, authToken, applicationId, tenantId, userLocale, activeNotificatonsHandler, ...platformNotificationOptions }) => {
    const { listFilesInFolder, getNotifications } = useAzureStorageBlobs({
        storageContainer,
        storageUrl,
        authToken,
    });
    const configNotification = {
        storageContainer,
        storageUrl,
        authToken,
        tenantId,
        applicationId,
    };
    const [activeNotifications, setActiveNotifications] = useState([]);
    const subscriptionTokensRef = useRef([]);
    const recipients = getNotificationSocketTopicRecipients({ tenantId, applicationId });
    useMemo(() => activeNotificatonsHandler === null || activeNotificatonsHandler === void 0 ? void 0 : activeNotificatonsHandler(activeNotifications), [activeNotifications]);
    const handleBlob = async (blob) => {
        logger.debug('processing blob', blob);
        const result = blobNameToTimeDistribution(blob.name);
        logger.debug('blob processed', blob, result);
        switch (result.distribution) {
            case NotificationTimeDistributionTypes.NOTIFIABLE:
                try {
                    const notifications = await getNotifications(blob.name);
                    notifications.forEach((notification) => {
                        subscriptionTokensRef.current.push(window.setTimeout(() => setActiveNotifications(deleteNotification(notification)), result.duration));
                    });
                    return notifications;
                }
                catch (e) {
                    logger.error('unable to get notifications from blob', e, blob, result);
                    return [];
                }
            case NotificationTimeDistributionTypes.FUTURE:
                subscriptionTokensRef.current.push(window.setTimeout(() => handleBlob(blob), result.missingTime));
                return [];
            default:
                return [];
        }
    };
    const handleSocketNotification = useCallback((socketMessage) => {
        logger.debug('processing socket notification', socketMessage);
        const socketNotification = socketMessage.content;
        const result = notificationToTimeDistribution(socketNotification);
        logger.debug('socket notification processed', socketMessage, result);
        switch (result.distribution) {
            case NotificationTimeDistributionTypes.NOTIFIABLE:
                setActiveNotifications(addNotification(socketNotification));
                subscriptionTokensRef.current.push(window.setTimeout(() => setActiveNotifications(deleteNotification(socketNotification)), result.duration));
                break;
            case NotificationTimeDistributionTypes.FUTURE:
                subscriptionTokensRef.current.push(window.setTimeout(() => setActiveNotifications(addNotification(socketNotification)), result.missingTime));
                break;
        }
    }, [setActiveNotifications]);
    const handleSocketNotificationDelete = useCallback((socketMessage) => {
        logger.debug('processing socket notification delete', socketMessage);
        const { notificationId: id } = socketMessage.content;
        setActiveNotifications(deleteNotification({ id }));
    }, [setActiveNotifications]);
    useEffect(() => {
        const processStorageBlobs = async () => {
            const blobs = listFilesInFolder(STORAGE_FOLDER_VIRTUAL_DELIMITER, {
                prefix: blobFolderUriBuilder({ tenantId, applicationId }),
            });
            let notifications = [];
            try {
                for await (const blob of blobs) {
                    notifications = [...notifications, ...(await handleBlob(blob))];
                }
            }
            catch (e) {
                logger.error(e);
            }
            setActiveNotifications(notifications);
        };
        processStorageBlobs();
        initializeDOMPurify();
        return () => {
            subscriptionTokensRef.current.forEach(clearTimeout);
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(SubscribeSocketTopic, { topic: PLATFORM_NOTIFICATIONS_SOCKET_TOPIC, recipients: recipients, onMessage: handleSocketNotification }),
        React.createElement(SubscribeSocketTopic, { topic: PLATFORM_NOTIFICATIONS_DELETE_SOCKET_TOPIC, recipients: recipients, onMessage: handleSocketNotificationDelete }),
        React.createElement(ActiveNotificationList, { notifications: activeNotifications, ...platformNotificationOptions, tenantId: tenantId, config: configNotification, userLocale: userLocale })));
};
//# sourceMappingURL=PlatformNotifications.js.map